/**
 * DashboardPage — B1.11 BusinessDashboard smart wrapper.
 *
 * Architecture (`.cursor/rules/lovable-boundary.mdc`):
 *   - This page owns data fetching, tenant scoping, period state, insight
 *     dismissal and navigation callbacks.
 *   - The Lovable-delivered presentational primitives live under
 *     `shared/ui/dashboard/` and are composed by `BusinessDashboard`.
 *   - `features/dashboard/lovable-adapter.ts` translates i18next resources
 *     into the `DashboardLabels` contract the composite expects.
 *
 * Snapshot shape between the feature layer and `shared/ui/dashboard/` is
 * intentionally identical (frozen data contract — see the Lovable brief in
 * `docs/_context/PETOTA_BUSINESS_LOVABLE_BRIEF_B1_11_BUSINESS_DASHBOARD.md`),
 * so we pass `data` through after shallow-spreading the readonly arrays.
 */
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useCurrentBranch } from '../shared/context/TenantContext';
import {
  buildDashboardLabels,
  useDashboardSnapshot,
  useDismissInsight,
  type DashboardAiInsight,
  type DashboardPeriod,
  type DashboardQuickAction,
  type DashboardSnapshot,
  type DashboardUpcomingBooking,
} from '../features/dashboard';
import { BusinessDashboard } from '../shared/ui/dashboard/BusinessDashboard';
import type { DashboardSnapshot as UiDashboardSnapshot } from '../shared/ui/dashboard/types';

/**
 * Turn the feature-layer snapshot (all arrays `readonly`) into the
 * mutable-array shape that the presentational composite expects. This is a
 * pure shallow clone of the top-level collections — inner objects stay
 * structurally identical.
 */
function toUiSnapshot(snap: DashboardSnapshot): UiDashboardSnapshot {
  return {
    branchId: snap.branchId,
    branchName: snap.branchName,
    organizationName: snap.organizationName,
    period: snap.period,
    stats: [...snap.stats],
    revenueSeries: [...snap.revenueSeries],
    aiInsights: [...snap.aiInsights],
    upcomingBookings: [...snap.upcomingBookings],
    recentReviews: [...snap.recentReviews],
    staffLeaderboard: [...snap.staffLeaderboard],
    occupancy: [...snap.occupancy],
    quickActions: [...snap.quickActions],
    updatedAt: snap.updatedAt,
  };
}

export function DashboardPage(): JSX.Element {
  const { t } = useTranslation(['dashboard']);
  const navigate = useNavigate();
  const branch = useCurrentBranch();
  const [period, setPeriod] = useState<DashboardPeriod>('7d');

  const { data, isLoading, isError, error, refetch } = useDashboardSnapshot(
    branch?.branchId,
    period,
  );
  const dismiss = useDismissInsight(branch?.branchId, period);

  const labels = useMemo(() => buildDashboardLabels(t), [t]);

  const onInsightCta = useCallback(
    (insight: DashboardAiInsight) => navigate(insight.ctaHref),
    [navigate],
  );
  const onInsightDismiss = useCallback(
    (insight: DashboardAiInsight) => dismiss.mutate(insight.id),
    [dismiss],
  );
  const onQuickAction = useCallback(
    (action: DashboardQuickAction) => navigate(action.href),
    [navigate],
  );
  const onBookingClick = useCallback(
    (booking: DashboardUpcomingBooking) =>
      navigate(`/calendar?booking=${booking.id}`),
    [navigate],
  );
  const onViewAllBookings = useCallback(() => navigate('/calendar'), [navigate]);
  const onViewAllReviews = useCallback(() => navigate('/reviews'), [navigate]);
  const onRetry = useCallback(() => {
    void refetch();
  }, [refetch]);

  const uiSnapshot = useMemo(
    () => (data ? toUiSnapshot(data) : undefined),
    [data],
  );

  // BusinessDashboard renders its own skeleton / error states, but it needs a
  // snapshot structure to mount. While we're still loading the very first
  // payload we let the composite drive the skeleton.
  const renderSnapshot: UiDashboardSnapshot = uiSnapshot ?? {
    branchId: branch?.branchId ?? '',
    branchName: branch?.branchName ?? '',
    organizationName: branch?.organizationName ?? '',
    period,
    stats: [],
    revenueSeries: [],
    aiInsights: [],
    upcomingBookings: [],
    recentReviews: [],
    staffLeaderboard: [],
    occupancy: [],
    quickActions: [],
    updatedAt: new Date().toISOString(),
  };

  const uiError = isError
    ? {
        message:
          error instanceof Error
            ? error.message
            : labels.states.errorDescription,
      }
    : null;

  return (
    <BusinessDashboard
      snapshot={renderSnapshot}
      period={period}
      onPeriodChange={setPeriod}
      labels={labels}
      onInsightCta={onInsightCta}
      onInsightDismiss={onInsightDismiss}
      onBookingClick={onBookingClick}
      onViewAllBookings={onViewAllBookings}
      onViewAllReviews={onViewAllReviews}
      onQuickAction={onQuickAction}
      isLoading={isLoading}
      error={uiError}
      onRetry={onRetry}
      dataTestId="business-dashboard"
    />
  );
}
